import { Link, router } from "@inertiajs/react";
import { useState, useMemo } from "react";
import { Briefcase, TrendingUp } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";

interface Submission {
  id: number;
  candidate_id: number;
  candidate_name: string;
  firm_name: string;
  job_title: string | null;
  status: string;
  submission_method: string;
  submitted_at: string | null;
  created_at: string;
  auto_generated_at: string | null;
}

interface Stats {
  total_active: number;
  in_interview: number;
  offers: number;
  conversion_rate: number;
}

interface CandidateOption { id: number; full_name: string; }

interface Props {
  submissions: Submission[];
  stats: Stats;
  candidates: CandidateOption[];
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  drafted:      { label: "Drafted",       className: "bg-gray-100 text-gray-500" },
  submitted:    { label: "Submitted",     className: "bg-blue-50 text-blue-700" },
  screening:    { label: "Screening",     className: "bg-amber-50 text-amber-700" },
  full_round:   { label: "Full Round",    className: "bg-purple-50 text-purple-700" },
  offer_placed: { label: "Offer/Placed",  className: "bg-green-50 text-green-700" },
  rejected:     { label: "Rejected",      className: "bg-red-50 text-red-600" },
  withdrawn:    { label: "Withdrawn",     className: "bg-gray-50 text-gray-400" },
};

const METHOD_LABELS: Record<string, string> = {
  email_only:        "Email",
  portal_plus_email: "Portal + Email",
};

function fmt(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const ALL_STATUSES = Object.keys(STATUS_CONFIG);

export default function SubmissionsIndex({ submissions, stats, candidates }: Props) {
  const [statusFilter, setStatusFilter]       = useState("all");
  const [candidateFilter, setCandidateFilter] = useState("");
  const [firmFilter, setFirmFilter]           = useState("");
  const [autoOnly, setAutoOnly]               = useState(false);

  const filtered = useMemo(() => submissions.filter((s) => {
    if (statusFilter !== "all" && s.status !== statusFilter) return false;
    if (candidateFilter && s.candidate_id !== Number(candidateFilter)) return false;
    if (firmFilter && !s.firm_name.toLowerCase().includes(firmFilter.toLowerCase())) return false;
    if (autoOnly && !s.auto_generated_at) return false;
    return true;
  }), [submissions, statusFilter, candidateFilter, firmFilter, autoOnly]);

  function applyServerFilter() {
    router.get("/submissions", {
      status:       statusFilter !== "all" ? statusFilter : undefined,
      candidate_id: candidateFilter || undefined,
      firm:         firmFilter || undefined,
    }, { replace: true });
  }

  return (
    <AppLayout title="Submissions">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Submissions</h1>
          <p className="text-sm text-gray-500 mt-0.5">All candidate submissions across firms</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Active",       value: stats.total_active,    icon: "🔵" },
          { label: "In Interview", value: stats.in_interview,    icon: "🟡" },
          { label: "Offers",       value: stats.offers,          icon: "🟢" },
          { label: "Conversion",   value: `${stats.conversion_rate}%`, icon: "📈" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <span>{s.icon}</span>
              <span className="text-xs text-gray-500 uppercase tracking-wide">{s.label}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
          <button onClick={() => setStatusFilter("all")}
            className={`px-3 py-1.5 font-medium transition-colors ${statusFilter === "all" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"}`}>
            All
          </button>
          {ALL_STATUSES.map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 font-medium capitalize transition-colors ${statusFilter === s ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"}`}>
              {STATUS_CONFIG[s].label}
            </button>
          ))}
        </div>

        <select value={candidateFilter} onChange={(e) => setCandidateFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-600 bg-white focus:outline-none">
          <option value="">All Candidates</option>
          {candidates.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
        </select>

        <input type="text" placeholder="Filter by firm..." value={firmFilter}
          onChange={(e) => setFirmFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none w-44" />

        <button onClick={() => setAutoOnly(!autoOnly)}
          className={`text-sm px-3 py-1.5 rounded-lg border font-medium transition-colors ${autoOnly ? "bg-green-50 border-green-300 text-green-700" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}>
          ⚡ Auto-generated
        </button>

        <span className="text-sm text-gray-400 ml-auto">{filtered.length} submission{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Briefcase className="w-8 h-8 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No submissions match the current filters.</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Candidate</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Firm</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((s) => {
                const cfg = STATUS_CONFIG[s.status] ?? STATUS_CONFIG.drafted;
                return (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5">
                      <Link href={`/submissions/${s.id}`} className="text-sm font-medium text-gray-900 hover:text-green-700">
                        {s.candidate_name}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-700">{s.firm_name}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-500">{s.job_title || <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${cfg.className}`}>{cfg.label}</span>
                      {s.auto_generated_at && (
                        <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded bg-green-50 text-green-600 font-medium" title="Auto-generated by job alert match">⚡</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{METHOD_LABELS[s.submission_method] ?? s.submission_method}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-gray-400">{fmt(s.submitted_at || s.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </AppLayout>
  );
}
